import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import Card from '../../components/ui/Card.jsx'
import Button, { IconButton } from '../../components/ui/Button.jsx'
import Badge from '../../components/ui/Badge.jsx'
import { Modal } from '../../components/ui/Modal.jsx'
import { Field, Input } from '../../components/ui/Field.jsx'
import { useFeedback } from '../../components/ui/feedback.jsx'
import { useCompany } from '../../context/CompanyContext.jsx'
import api from '../../lib/api.js'
import { isValidIban, cleanIban, formatIban } from '../../lib/validation.js'

/*
 * Billing settings — bank accounts CRUD + invoice numbering. Inventory §5
 * Settings tab. Numbering uses PUT /companies/:id/invoice-numbering (the
 * correct path, fixing the legacy absolute-URL bug) with a live preview.
 */
export default function BillingTab() {
  const { t } = useTranslation()
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 18, alignItems: 'start' }}>
      <BankAccounts t={t} />
      <InvoiceNumbering t={t} />
    </div>
  )
}

function BankAccounts({ t }) {
  const { currentCompanyId } = useCompany()
  const { confirm, toast } = useFeedback()
  const [accounts, setAccounts] = useState([])
  const [modal, setModal] = useState(null)

  const load = useCallback(() => {
    if (!currentCompanyId) return
    api.get(`/companies/${currentCompanyId}/bank-accounts`).then(({ data }) => setAccounts(data?.bank_accounts || []))
  }, [currentCompanyId])
  useEffect(() => { load() }, [load])

  const remove = async (a) => {
    const ok = await confirm({ tone: 'danger', destructive: true, title: t('settings.bank.confirmDelete'), message: a.iban, confirmLabel: t('common.delete'), cancelLabel: t('common.cancel') })
    if (!ok) return
    try { await api.delete(`/companies/${currentCompanyId}/bank-accounts/${a.id}`); toast(t('settings.bank.deleted'), 'success'); load() }
    catch { toast(t('settings.errorGeneric'), 'error') }
  }

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 16 }}>{t('settings.bank.title')}</h3>
        <Button variant="primary" icon="plus" onClick={() => setModal({})}>{t('settings.bank.add')}</Button>
      </div>
      {accounts.length === 0 && <div style={{ color: 'var(--color-text-muted)', fontSize: 13.5 }}>{t('settings.bank.empty')}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
        {accounts.map((a) => (
          <div key={a.id} style={{ border: '1px solid var(--color-border)', borderRadius: 12, padding: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
              <div style={{ fontWeight: 600, color: 'var(--color-ink)' }}>{a.account_name}{a.is_default && <span style={{ marginLeft: 8 }}><Badge tone="info">{t('parties.default')}</Badge></span>}</div>
              <div style={{ display: 'flex', gap: 6 }}>
                <IconButton icon="pencil" variant="primary" title={t('common.edit')} onClick={() => setModal({ account: a })} />
                <IconButton icon="trash" variant="danger" title={t('common.delete')} onClick={() => remove(a)} />
              </div>
            </div>
            <div style={{ fontFamily: 'monospace', fontSize: 13.5 }}>{formatIban(a.iban)}</div>
            <div style={{ fontSize: 12.5, color: 'var(--color-text-muted)' }}>{a.bic}{a.bank_name ? ` · ${a.bank_name}` : ''}</div>
          </div>
        ))}
      </div>
      {modal && <BankModal t={t} companyId={currentCompanyId} account={modal.account} accounts={accounts} onClose={() => setModal(null)} onSaved={() => { setModal(null); toast(t('settings.bank.saved'), 'success'); load() }} />}
    </Card>
  )
}

function BankModal({ t, companyId, account, accounts, onClose, onSaved }) {
  const isEdit = !!account
  const [f, setF] = useState(account ? { ...account } : { account_name: '', bank_name: '', account_holder: '', iban: '', bic: '', is_default: accounts.length === 0 })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }))
  const ibanInvalid = f.iban && !isValidIban(f.iban)

  const submit = async (e) => {
    e.preventDefault()
    setError('')
    if (!isValidIban(f.iban)) { setError(t('settings.bank.ibanInvalid')); return }
    const norm = cleanIban(f.iban)
    if (accounts.some((a) => a.id !== account?.id && cleanIban(a.iban) === norm)) { setError(t('settings.bank.ibanDuplicate')); return }
    setSaving(true)
    const payload = { ...f, iban: norm }
    try {
      if (isEdit) await api.put(`/companies/${companyId}/bank-accounts/${account.id}`, payload)
      else await api.post(`/companies/${companyId}/bank-accounts`, payload)
      onSaved()
    } catch (e2) { setError(e2?.response?.data?.error || t('settings.errorGeneric')) } finally { setSaving(false) }
  }

  return (
    <Modal open onClose={onClose} maxWidth={520} closeOnBackdrop={false}>
      <form onSubmit={submit} style={{ padding: 24 }}>
        <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 18, marginBottom: 16 }}>{isEdit ? t('settings.bank.edit') : t('settings.bank.add')}</h3>
        {error && <div role="alert" style={{ background: 'var(--color-danger-bg)', color: 'var(--color-danger)', border: '1px solid #f3c9c9', borderRadius: 10, padding: '9px 12px', fontSize: 13, marginBottom: 14 }}>{error}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <Field label={t('settings.bank.accountName')} required><Input value={f.account_name} onChange={(e) => set('account_name', e.target.value)} required /></Field>
          <Field label={t('settings.bank.bankName')}><Input value={f.bank_name} onChange={(e) => set('bank_name', e.target.value)} /></Field>
        </div>
        <Field label={t('settings.bank.holder')}><Input value={f.account_holder} onChange={(e) => set('account_holder', e.target.value)} /></Field>
        <Field label="IBAN" required error={ibanInvalid ? t('settings.bank.ibanInvalid') : undefined}>
          <Input value={f.iban} onChange={(e) => set('iban', formatIban(e.target.value))} placeholder="BE68 5390 0754 7034" style={ibanInvalid ? { borderColor: 'var(--color-danger)' } : undefined} />
        </Field>
        <Field label="BIC" required><Input value={f.bic} onChange={(e) => set('bic', e.target.value.toUpperCase())} maxLength={11} /></Field>
        <label style={{ display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer', fontSize: 13.5, marginBottom: 16 }}>
          <input type="checkbox" checked={f.is_default} onChange={(e) => set('is_default', e.target.checked)} /> {t('settings.bank.setDefault')}
        </label>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <Button type="button" variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" variant="primary" icon="check" loading={saving}>{t('common.save')}</Button>
        </div>
      </form>
    </Modal>
  )
}

function InvoiceNumbering({ t }) {
  const { currentCompanyId } = useCompany()
  const { toast, notify } = useFeedback()
  const [data, setData] = useState(null)
  const [pattern, setPattern] = useState('')
  const [start, setStart] = useState(1)
  const [preview, setPreview] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!currentCompanyId) return
    api.get(`/companies/${currentCompanyId}/invoice-numbering`).then(({ data: d }) => {
      setData(d); setPattern(d.invoice_number_pattern || 'INV-%Y-%4d'); setStart(d.invoice_number_start ?? 1)
    })
  }, [currentCompanyId])

  useEffect(() => {
    if (!currentCompanyId || !pattern) return
    const id = setTimeout(() => {
      api.post(`/companies/${currentCompanyId}/invoice-numbering/preview`, { pattern, start_number: Number(start), sequence_number: Number(start) })
        .then(({ data: d }) => setPreview(d?.preview || '')).catch(() => setPreview(''))
    }, 300)
    return () => clearTimeout(id)
  }, [pattern, start, currentCompanyId])

  const save = async () => {
    setSaving(true)
    try { await api.put(`/companies/${currentCompanyId}/invoice-numbering`, { invoice_number_pattern: pattern, invoice_number_start: Number(start) }); toast(t('settings.numbering.saved'), 'success') }
    catch (e) { notify({ tone: 'error', title: t('settings.error'), message: e?.response?.data?.error || t('settings.errorGeneric') }) }
    finally { setSaving(false) }
  }

  if (!data) return null
  return (
    <Card>
      <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 16, marginBottom: 14 }}>{t('settings.numbering.title')}</h3>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14 }}>
        <Field label={t('settings.numbering.pattern')}><Input value={pattern} onChange={(e) => setPattern(e.target.value)} style={{ fontFamily: 'monospace' }} /></Field>
        <Field label={t('settings.numbering.start')}><Input type="number" min="1" value={start} onChange={(e) => setStart(e.target.value)} /></Field>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--color-bg-lilac)', borderRadius: 10, marginBottom: 14 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '.04em' }}>{t('settings.numbering.preview')}</span>
        <span style={{ fontFamily: 'var(--font-title)', fontWeight: 700, color: 'var(--color-ink)' }}>{preview || '—'}</span>
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--color-text-muted)', marginBottom: 14 }}>
        {Object.entries(data.available_substitutions || {}).slice(0, 6).map(([k, v]) => <div key={k}><code style={{ color: 'var(--color-primary)' }}>{k}</code> — {v}</div>)}
      </div>
      <Button variant="primary" icon="check" loading={saving} onClick={save}>{t('common.save')}</Button>
    </Card>
  )
}
